import {AlertCircle} from "lucide-react";

import {Alert, AlertTitle} from "@/components/ui/alert";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import type {AmenityConfig} from "@/src/modules/amenities/lib/amenity-config";
import {usePropertyAmenities} from "@/src/modules/amenities/hooks/use-property-amenities";
import type {PropertyAmenity} from "@/src/modules/amenities/types";

/** Form selections often only carry id + name until a full amenity is loaded. */
export type PropertyAmenityComboboxValue = Pick<
  PropertyAmenity,
  "property_amenity_id" | "name"
> &
  Partial<Omit<PropertyAmenity, "property_amenity_id" | "name">>;

type PropertyAmenitiesComboboxProps = {
  config: AmenityConfig;
  value: PropertyAmenityComboboxValue | null;
  onChange: (value: PropertyAmenityComboboxValue | null) => void;
  contextPropertyId?: number | null;
};

export function PropertyAmenitiesCombobox({
  config,
  value,
  onChange,
  contextPropertyId,
}: PropertyAmenitiesComboboxProps) {
  const {propertyAmenities, isFetching} = usePropertyAmenities({
    config,
    contextPropertyId,
  });

  const emptyLabel = `No ${config.labelFacilities.toLowerCase()} found`;

  if (!isFetching && !propertyAmenities.length) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertTitle>{emptyLabel}</AlertTitle>
      </Alert>
    );
  }

  return (
    <ComboboxGeneric<PropertyAmenityComboboxValue>
      items={propertyAmenities}
      value={value}
      onChange={onChange}
      getItemKey={(item) => `property-amenity-${item.property_amenity_id}`}
      getItemLabel={(item) => item.name}
      isItemEqual={(item, selected) => item.property_amenity_id === selected?.property_amenity_id}
      isLoading={isFetching}
      placeholder={`Select ${config.labelFacilitySingular.toLowerCase()}`}
      emptyMessage={emptyLabel}
    />
  );
}
